import { useState, useCallback, useEffect } from 'react';
import Drawer from '~/components/Drawer';
import { debounce } from 'lodash';
import { searchCampaigns } from '~/services/campaign';
import { CampaignSearchResult } from '~/types/campaigns';
import { useMutation } from '@tanstack/react-query';
import {
    createDraft,
    fetchCampaignAvailableContacts,
} from '~/services/fund-requests';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CampaignContactOption } from '~/types/fundrequests';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const AddFundRequestDrawer: React.FC<Props> = ({ isOpen, setIsOpen }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [data, setData] = useState<CampaignSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedCampaign, setSelectedCampaign] =
        useState<CampaignSearchResult | null>(null);
    const [availableContacts, setAvailableContacts] = useState<
        CampaignContactOption[]
    >([]);
    const [selectedCampaignContact, setSelectedCampaignContact] =
        useState<CampaignContactOption | null>(null);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const navigate = useNavigate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(
        debounce((term: string) => {
            (async () => {
                setLoading(true);
                const res = await searchCampaigns(term, true); // Pass true to filter for campaigns with NTAF ID
                setData(res.results);
                setLoading(false);
            })();
        }, 500),
        []
    );

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        debouncedFetch(newSearchTerm);
    };

    const resetAndCloseForm = () => {
        setIsOpen(false);
        setSearchTerm('');
        setData([]);
        setSelectedCampaign(null);
        setAvailableContacts([]);
        setSelectedCampaignContact(null);
    };

    // Fetch available contacts when campaign is selected
    useEffect(() => {
        const fetchAvailableContacts = async () => {
            if (selectedCampaign?.id) {
                setLoadingContacts(true);
                try {
                    const contacts = await fetchCampaignAvailableContacts(
                        selectedCampaign.id.toString()
                    );
                    setAvailableContacts(contacts);

                    // Auto-select primary contact if available
                    const primaryContact = contacts.find(
                        (contact: CampaignContactOption) =>
                            contact.is_primary_contact
                    );
                    if (primaryContact) {
                        setSelectedCampaignContact(primaryContact);
                    } else if (contacts.length === 1) {
                        // If only one contact, select them automatically
                        setSelectedCampaignContact(contacts[0]);
                    }
                } catch (error) {
                    console.error('Error fetching available contacts:', error);
                    toast.error('Failed to load available contacts');
                } finally {
                    setLoadingContacts(false);
                }
            } else {
                setAvailableContacts([]);
                setSelectedCampaignContact(null);
            }
        };

        fetchAvailableContacts();
    }, [selectedCampaign]);

    const draftFundRequest = useMutation({
        mutationFn: async () => {
            const res = await createDraft({
                campaign_id: selectedCampaign?.id,
                campaign_contact: selectedCampaignContact?.id,
            });
            return res;
        },
        onSuccess: (newFundRequest) => {
            toast.success('Fund request created successfully');
            navigate(`/fund-requests/${newFundRequest.id}`);
        },
        onError: () => {
            toast.error('Error creating fund request');
        },
    });

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
            <h2 className='mb-[28px] text-[22px]/[28px] font-bold'>
                Add Request
            </h2>

            <label className='text-[12px]/[14px] font-extrabold'>
                Fund Name{' '}
                <span className='mb-[3px] text-[10px]/[10px] text-[#F02D3A]'>
                    *
                </span>
            </label>
            <div className='mb-[5px] flex rounded-lg border border-[#949599] p-[10px]'>
                <input
                    className='w-full'
                    type='text'
                    value={searchTerm}
                    placeholder='Start typing name'
                    onChange={handleSearchTermChange}
                />
            </div>

            {loading ? (
                <div className='mt-4 text-center'>
                    <span>Loading...</span>
                </div>
            ) : (
                <table className='mt-[25px] w-full'>
                    <thead>
                        <tr className='bg-[#F4F4F5]'>
                            <th></th>
                            <th className='w-2/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                Fund Name
                            </th>
                            <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                NTAF ID
                            </th>
                            <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                Status
                            </th>
                            <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                Medical Reason
                            </th>
                            <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                Medical Issue
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((campaign) => (
                            <tr key={campaign?.id}>
                                <td className='p-[10px]'>
                                    <input
                                        type='radio'
                                        className='mt-[6px] h-[18px] w-[18px] shrink-0'
                                        name='selected_campaign'
                                        onChange={() =>
                                            setSelectedCampaign(campaign)
                                        }
                                    />
                                </td>
                                <td className='p-[10px]'>
                                    {campaign?.fund_name}
                                </td>
                                <td className='p-[10px]'>
                                    {campaign?.ntaf_id}
                                </td>
                                <td className='p-[10px]'>
                                    {campaign?.campaign_status?.name}
                                </td>
                                <td className='p-[10px]'>
                                    {campaign?.campaign_type?.name}
                                </td>
                                <td className='p-[10px]'>
                                    {campaign?.primary_medical_issue}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {selectedCampaign && (
                <div className='mt-[25px]'>
                    <label className='mb-2 block text-[12px]/[14px] font-extrabold'>
                        Campaign Contact
                        <span className='ml-1 text-[10px]/[10px] text-[#F02D3A]'>
                            *
                        </span>
                    </label>
                    <div className='mb-[5px] rounded-lg border border-[#949599]'>
                        {loadingContacts ? (
                            <div className='text-gray-500 p-[10px] text-center'>
                                Loading available contacts...
                            </div>
                        ) : availableContacts.length > 0 ? (
                            <select
                                className='w-full rounded-lg bg-white p-[10px]'
                                value={selectedCampaignContact?.id || ''}
                                onChange={(e) => {
                                    const contactId = e.target.value;
                                    if (contactId) {
                                        const contact = availableContacts.find(
                                            (c) => c.id.toString() === contactId
                                        );
                                        setSelectedCampaignContact(
                                            contact || null
                                        );
                                    } else {
                                        setSelectedCampaignContact(null);
                                    }
                                }}
                                required
                            >
                                <option value=''>
                                    Select a contact to receive notifications
                                </option>
                                {availableContacts.map((contact) => (
                                    <option key={contact.id} value={contact.id}>
                                        {contact.first_name} {contact.last_name}
                                        {contact.is_primary_contact
                                            ? ' (Primary Contact)'
                                            : contact.is_client
                                            ? ' (Client)'
                                            : ''}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className='text-red-500 p-[10px] text-center'>
                                No contacts with access found for this campaign.
                                Please add a team member with access or set a
                                primary contact before creating a fund request.
                            </div>
                        )}
                    </div>
                    <p className='text-gray-500 mb-4 text-[10px]/[12px]'>
                        Assign a contact from the campaign for{' '}
                        {selectedCampaign.fund_name} to receive any email
                        notifications related to the request.
                    </p>
                </div>
            )}

            <div className='mt-[30px]'>
                <button
                    className='m-auto mr-3 justify-self-end rounded-3xl border-[2px] border-black bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase hover:bg-black hover:text-white'
                    onClick={resetAndCloseForm}
                >
                    Cancel
                </button>
                {Array.isArray(data) && data.length && loading ? (
                    <button
                        disabled
                        className='m-auto mr-3 rounded-full bg-[#F4F4F5] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#949599] hover:bg-opacity-90'
                    >
                        Continue
                    </button>
                ) : (
                    <button
                        className='disabled:bg-gray-300 disabled:text-gray-500 m-auto mr-3 rounded-full bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#131212] hover:bg-opacity-90 disabled:cursor-not-allowed'
                        onClick={() => {
                            if (!selectedCampaign) {
                                toast.error('Please select a campaign');
                                return;
                            }
                            if (!selectedCampaignContact) {
                                toast.error('Please select a campaign contact');
                                return;
                            }
                            if (availableContacts.length === 0) {
                                toast.error(
                                    'This campaign has no available contacts. Please add a team member with access or set a primary contact before creating a fund request.'
                                );
                                return;
                            }
                            draftFundRequest.mutate();
                        }}
                        disabled={
                            !selectedCampaign ||
                            !selectedCampaignContact ||
                            availableContacts.length === 0 ||
                            draftFundRequest.isPending
                        }
                    >
                        {draftFundRequest.isPending ? `Saving` : `Continue`}
                    </button>
                )}
            </div>
        </Drawer>
    );
};

export default AddFundRequestDrawer;
